/**
 * The whole loaded PostHog flag map, so callers can tell ABSENT from OFF.
 *
 * `usePostHogFeatureFlag` (and PostHog's own `getFeatureFlag`) cannot make
 * that distinction: once flags have loaded, a key PostHog has never heard of
 * comes back as a real boolean `false`, identical to a flag someone
 * deliberately switched off. `undefined` from the single-flag getter means
 * "still loading", and nothing else.
 *
 * Measured, not assumed (SE-8200): against the dev PostHog project, with
 * `compliance-tab-beta-dev` not created, `getFeatureFlag` returned
 * `boolean:false` while `getFeatureFlags()` held 25 keys, none of them that
 * one. So presence in this map is the only available evidence that a flag
 * exists at all.
 *
 * Prefer {@link usePostHogFeatureFlag} for ordinary rollout gates, where
 * absent and off should behave the same. Reach for this only when the two
 * must differ — a kill-switch that defaults ON, for instance.
 * @module hooks/usePostHogFeatureFlagMap
 */
import { useFeatureFlags } from "posthog-react-native";

/** The loaded flag map, or `undefined` while flags are still in flight. */
export type PostHogFlagMap = Readonly<Record<string, boolean | string>>;

/**
 * Reads every evaluated PostHog flag.
 * @returns The flag map, or `undefined` before the first evaluation lands.
 */
const usePostHogFeatureFlagMap = (): PostHogFlagMap | undefined =>
  useFeatureFlags();

export default usePostHogFeatureFlagMap;
